<template>
	<div class="FieldPhoneValidate">
		<FieldLayout :title="title" :error="phoneNum_error || isPhoneValid_error">
			<InputText
				:value="phoneFormat(phoneNum)"
				maxlength="13"
				inputmode="tel"
				:placeholder="PLACEHOLDER_NUMBERS_ONLY_EXCEPT_DASH"
				:disabled="isPhoneValid || isValidating || hasPhoneNum"
				:show-error-icon="phoneNum_error || isPhoneValid_error"
				:class="{ error: phoneNum_error || isPhoneValid_error }"
				@input="onInputContact"
				@keyup.enter="requestCertNum"
			/>
			<button
				v-if="!hasPhoneNum"
				type="button"
				:disabled="!!phoneNum_error || (phoneNum?.length ?? 0) < 13"
				class="c_button primary-fill radius-x-small submit"
				@click="requestCertNum"
			>
				{{ isValidating ? '재전송' : '전송' }}
			</button>
			<button v-else class="c_button primary-fill radius-x-small" @click="hasPhoneNum = false">번호변경</button>
		</FieldLayout>

		<FieldLayout
			class="certNumField"
			@input="
				() => {
					if (isPhoneValid) emit('update:isPhoneValid', false);
				}
			"
		>
			<div class="certNumInputBox">
				<InputNumber
					id="certNumInputEl"
					v-model="certNum"
					maxlength="6"
					inputmode="numeric"
					pattern="[0-9]*"
					:placeholder="INPUT_CODE"
					class="certNumInput"
					autocomplete="one-time-code"
					@input="onInputCode"
					@keyup.enter="onValidateCertNum"
				/>
				<p v-if="isValidating">{{ `${minutesLeft}:${secondLeft}` }}</p>
			</div>
			<button
				type="button"
				:disabled="certNum.length < 6 || isPhoneValid"
				class="c_button primary-fill radius-x-small"
				@click="onValidateCertNum"
			>
				인증
			</button>
		</FieldLayout>
	</div>
</template>

<script setup lang="ts">
// core
import { nextTick, onUnmounted, ref } from 'vue';
// util
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// api
import { useCheckCertificationApi, useCheckIdentificationNumberApi, useSendAuthApi } from '@/api/auth';
// component
import FieldLayout from '@/components/form/FieldLayout.vue';
import {
	COMPANY_TITLE,
	ERROR_DUPLICATE_PHONE_NUMBER,
	ERROR_EXCEEDED_VERIFICATION_LIMIT,
	ERROR_SEND_VERIFICATION_FAILED,
	ERROR_VERIFICATION_CODE_MISMATCH,
	INPUT_CODE,
	MESSAGE_POST_CODE,
	MESSAGE_POST_RE_CODE,
	MESSAGE_RESEND_AVAILABLE_AFTER_20_SECONDS,
	MESSAGE_VERIFICATION_COMPLETED,
	PLACEHOLDER_NUMBERS_ONLY_EXCEPT_DASH,
} from '@/constants/title';
import { usePopupStore } from '@/stores/popup';
// store
import { useToastStore } from '@/stores/toast';
import { useTimer } from '@/utils/directive/composables/useTimer';
import { phoneFormat } from '@/utils/function/format';

import InputNumber from '../atom/input/InputNumber.vue';
// image
import InputText from '../atom/input/InputText.vue';
//인증 메일 발송 후 타이머

const TIMER = 180; // Default 3분
const { restartTimer, startTimer, minutesLeft, secondLeft, endTimer } = useTimer(TIMER);

dayjs.extend(duration);
const popupStore = usePopupStore();
const toastStore = useToastStore();
const props = withDefaults(
	defineProps<{
		title?: string; // 필드 제목
		required?: boolean; // 필수 입력 여부(디자인적 요소임)
		phoneNum: string; // 휴대폰번호
		phoneNum_error?: string; // 휴대폰번호 에러 메시지
		isPhoneValid: boolean; // 휴대폰번호 인증 여부
		isPhoneValid_error?: string; // 휴대폰번호 인증 여부 에러 메시지
		phoneType: 'SignUp' | 'Check';
	}>(),
	{
		title: '휴대폰번호',
		isSignUp: 'SignUp',
	}
);
const emit = defineEmits(['update:phoneNum', 'update:isPhoneValid', 'update:isPhoneCode', 'update:set-field-error']);
const hasPhoneNum = ref(false);
const certNum = ref('');
const isValidating = ref(false); // TODO: 인증번호 확인 로직 추가

const { mutateAsync: sendSms, isPending: isSendSmsPending } = useSendAuthApi();
const { mutateAsync: checkCertification, isPending: isCheckCertificationPending } = useCheckCertificationApi();
const { mutateAsync: checkIdentificationNumber } = useCheckIdentificationNumberApi();

const onInputContact = (event: Event) => {
	const target = event.target as HTMLInputElement;
	target.value = phoneFormat(target.value);
	emit('update:phoneNum', phoneFormat(target.value));
};

const onInputCode = (event: Event) => {
	const target = event.target as HTMLInputElement;
	emit('update:isPhoneCode', target.value);
};
// 인증번호 유효시간 타이머 종료
const stopCertNumTimer = () => {
	isValidating.value = false;
	endTimer();
};

// 인증번호 발송
const requestCertNum = async () => {
	if (isSendSmsPending.value || !!props.phoneNum_error || (props.phoneNum?.length ?? 0) < 13) {
		return;
	}
	if (isValidating.value && minutesLeft.value === '2' && secondLeft.value >= '40') {
		toastStore.openToast({
			subContent: COMPANY_TITLE,
			content: MESSAGE_RESEND_AVAILABLE_AFTER_20_SECONDS,
		});

		return;
	}
	const res = await sendSms({ member_contact: props.phoneNum });
	if (res.data.success) {
		if (!isValidating.value) {
			toastStore.openToast({
				subContent: COMPANY_TITLE,
				content: MESSAGE_POST_CODE,
			});
			isValidating.value = true;
			startTimer();
		} else {
			toastStore.openToast({
				subContent: COMPANY_TITLE,
				content: MESSAGE_POST_RE_CODE,
			});
			restartTimer();
		}

		await nextTick();
		const certNumInputEl = document.getElementById('certNumInputEl') as HTMLInputElement;
		certNumInputEl?.focus(); // 인증번호 입력창으로 포커스 이동
	} else {
		if (res.data.error.code == '59') {
			popupStore.openPopup({
				content: COMPANY_TITLE,
				subtext: ERROR_EXCEEDED_VERIFICATION_LIMIT,
			});
			return;
		}
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: ERROR_SEND_VERIFICATION_FAILED,
		});
	}
};

// 인증번호 확인
const onValidateCertNum = async () => {
	if (isCheckCertificationPending.value || certNum.value.length < 6) {
		return;
	}
	if (props.phoneType === 'SignUp') {
		const res = await checkCertification({
			phonenum: props.phoneNum,
			identification_no: certNum.value,
		});
		if (res.data.success) {
			stopCertNumTimer();
			toastStore.openToast({
				subContent: COMPANY_TITLE,
				content: MESSAGE_VERIFICATION_COMPLETED,
			});
			emit('update:isPhoneValid', true);
		} else {
			if (res.data.error.code == '81') {
				hasPhoneNum.value = true;
				isValidating.value = false;
				emit('update:set-field-error', ERROR_DUPLICATE_PHONE_NUMBER);
			} else {
				popupStore.openPopup({
					content: COMPANY_TITLE,
					subtext: ERROR_VERIFICATION_CODE_MISMATCH,
				});
			}
		}
	} else if (props.phoneType === 'Check') {
		const res = await checkIdentificationNumber({
			phonenum: props.phoneNum,
			identification_no: certNum.value,
		});
		if (res.data.success) {
			stopCertNumTimer();
			toastStore.openToast({
				subContent: COMPANY_TITLE,
				content: MESSAGE_VERIFICATION_COMPLETED,
			});
			emit('update:isPhoneValid', true);
		} else {
			if (res.data.error.code == '81') {
				hasPhoneNum.value = true;
				isValidating.value = false;
				emit('update:set-field-error', ERROR_DUPLICATE_PHONE_NUMBER);
			} else {
				popupStore.openPopup({
					content: COMPANY_TITLE,
					subtext: ERROR_VERIFICATION_CODE_MISMATCH,
				});
			}
		}
	}
};

onUnmounted(() => endTimer());
</script>

<style scoped lang="scss">
.FieldPhoneValidate {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	.certNumField {
		.certNumInputBox {
			flex-grow: 1;
			position: relative;
			.certNumInput {
				padding-right: 8.5ch;
			}
			p {
				position: absolute;
				right: 2ch;
				top: 50%;
				transform: translateY(-50%);
				color: var(--color-danger);
				font-size: var(--font-s-small);
			}
		}
	}
	.notification {
		display: flex;
		align-items: flex-start;
		gap: var(--space-x-small);
		color: var(--color-font-mid);
		svg {
			margin-top: 0.1em;
			width: 1.2em;
			height: 1.2em;
			fill: currentColor;
		}
		p {
			font-size: var(--font-size-small);
			line-height: 1.4;
		}
	}
}
.FieldLayout .field button {
	width: 7.7rem;
}
</style>
